import { CartLinesUpdateEvent } from '@shopify/events';
import { fetchConfig } from '@theme/utilities';

/**
 * Sets a variant's cart quantity through /cart/update.js and hands the re-rendered cart
 * sections to Horizon's cart-items components via CartLinesUpdateEvent, so the drawer,
 * cart page and header bubble stay in sync without a page reload.
 * @param {HTMLElement} source - Element that dispatches the event
 * @param {string} variantId
 * @param {number} quantity
 * @param {'add' | 'update' | 'remove'} action
 */
async function setVariantQuantity(source, variantId, quantity, action) {
  const sectionIds = new Set();
  for (const el of document.querySelectorAll('cart-items-component')) {
    if (el instanceof HTMLElement && el.dataset.sectionId) sectionIds.add(el.dataset.sectionId);
  }

  const deferred = CartLinesUpdateEvent.createPromise();
  source.dispatchEvent(
    new CartLinesUpdateEvent({
      action,
      context: 'cart',
      lines: [{ merchandiseId: variantId, quantity }],
      promise: deferred.promise,
    })
  );

  try {
    const body = JSON.stringify({
      updates: { [variantId]: quantity },
      sections: Array.from(sectionIds).join(','),
      sections_url: window.location.pathname,
    });
    const response = await fetch(`${Theme.routes.cart_update_url}.js`, fetchConfig('json', { body }));
    const data = await response.json();
    if (!response.ok || data.status) throw new Error(data.description || data.message || 'Cart update failed');

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(data),
      detail: { sections: data.sections ?? undefined, items: data.items, itemCount: data.item_count, didError: false },
    });
  } catch (error) {
    deferred.reject(error);
    throw error;
  }
}

class GiftWrap extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input[type="checkbox"]');
    this.input?.addEventListener('change', this.#onChange);
  }

  disconnectedCallback() {
    this.input?.removeEventListener('change', this.#onChange);
  }

  #onChange = async () => {
    if (!this.input || !this.dataset.variantId) return;
    const checked = this.input.checked;
    this.setAttribute('aria-busy', 'true');
    try {
      await setVariantQuantity(this, this.dataset.variantId, checked ? 1 : 0, checked ? 'add' : 'remove');
    } catch {
      this.input.checked = !checked;
    } finally {
      this.removeAttribute('aria-busy');
    }
  };
}

class AddButton extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button[data-variant-id]');
    this.button?.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.#onClick);
  }

  #onClick = async () => {
    const button = this.button;
    if (!button || button.getAttribute('aria-busy') === 'true') return;
    button.setAttribute('aria-busy', 'true');
    try {
      await setVariantQuantity(this, button.dataset.variantId ?? '', 1, 'add');
    } catch {
      button.removeAttribute('aria-busy');
    }
  };
}

if (!customElements.get('ct-gift-wrap')) customElements.define('ct-gift-wrap', GiftWrap);
if (!customElements.get('ct-add-button')) customElements.define('ct-add-button', AddButton);
